/**
 * Как делится свободный остаток.
 *
 * Режим отвечает ровно на один вопрос: что делать с деньгами, которые
 * остались **после** обязательных платежей, обычной жизни и переноса через
 * ближайший приход. Ни защищённый слой, ни жизнь, ни перенос от режима не
 * зависят — иначе выбор «быстрее к целям» означал бы «меньше есть», а это не
 * выбор, а ловушка.
 *
 * Таблица живёт здесь одна на всё приложение. До этого модуля их было две:
 * месячный план делил остаток у `goals_first` как 25/75, а оперативная
 * раскладка — как 30/70. Один и тот же человек с одним и тем же режимом
 * видел два разных числа на соседних экранах, и объяснить это было нечем.
 *
 * Проценты — внутренняя механика. Человеку показываются рубли и последствия:
 * «свободные траты 5 000 ₽, в цели 15 000 ₽, подушка будет готова примерно в январе».
 */
import { ZERO, add, clampPos, formatMoney, minM, share, sub, type Money } from './money.js';
import { goalEta, orderGoals } from './goals.js';
import type { PlainDate } from './date.js';
import type { Goal } from './types.js';
import type { PlanMode } from './plan.js';

/** Доли третьего слоя. Сумма каждой пары — единица. */
export interface ModeSplit {
  /** Доля остатка, которая достаётся «Свободные траты». */
  self: number;
  /** Доля остатка, которая уходит целям. */
  goals: number;
}

export const MODE_SPLIT: Record<PlanMode, ModeSplit> = {
  live_now: { self: 0.7, goals: 0.3 },
  balanced: { self: 0.5, goals: 0.5 },
  goals_first: { self: 0.25, goals: 0.75 },
};

/** Все режимы в порядке показа: от «сейчас» к «целям». */
export const MODES: PlanMode[] = ['live_now', 'balanced', 'goals_first'];

/** Названия, которыми режим зовут человеку. Без процентов. */
export const MODE_TITLE: Record<PlanMode, string> = {
  live_now: 'Больше оставить на сейчас',
  balanced: 'Баланс',
  goals_first: 'Быстрее к целям',
};

/** Режим по умолчанию. Отсутствие сохранённого значения читается как он. */
export const DEFAULT_MODE: PlanMode = 'balanced';

/** Сколько остатка достаётся «Свободные траты» при этом режиме. */
export const selfShare = (rest: Money, mode: PlanMode): Money =>
  share(rest, MODE_SPLIT[mode].self);

/** Сколько остатка достаётся целям при этом режиме. */
export const goalsShare = (rest: Money, mode: PlanMode): Money =>
  share(rest, MODE_SPLIT[mode].goals);

/** Что режим делает с деньгами человека — в рублях и сроках. */
export interface ModeOutlook {
  mode: PlanMode;
  title: string;
  /** Сколько достанется «Свободные траты». */
  self: Money;
  /** Сколько уйдёт целям: договор плюс ускорение. */
  goals: Money;
  /**
   * Сколько останется нераспределённым.
   *
   * Строка обязательная, а не «если останется». Без неё карточка не
   * сходится: при фиксированных взносах цель упирается в свой месячный
   * договор, и десятки тысяч рублей исчезали с экрана молча.
   */
  unallocated: Money;
  /** Поимённо: какая цель сколько получит и когда закроется. */
  byGoal: {
    goalId: string;
    name: string;
    amount: Money;
    /** Когда закроется при таком взносе. `null` — посчитать не из чего. */
    eta: PlainDate | null;
    /** Почему цель не получает больше. Пусто, когда ограничений нет. */
    why?: string;
  }[];
}

/** Что вообще можно сказать человеку о выборе режима. */
export interface ModeChoice {
  outlooks: ModeOutlook[];
  /** Остаток третьего слоя: он один на все режимы. */
  rest: Money;
  /** Невыполненная часть месячных договоров — одинакова во всех режимах. */
  committed: Money;
  /**
   * Есть ли цель, способная принять **сверх** месячного договора.
   *
   * Без неё «быстрее к целям» ничего не ускоряет: фиксированный взнос —
   * договор, и поднимать его молча нельзя. Тогда выбор режима надо не
   * показывать как три пути к целям, а честно сказать, что он ничего не
   * изменит.
   */
  accelerates: boolean;
  /** Есть ли вообще цели, которым нужны деньги в этом месяце. */
  hasGoals: boolean;
}

export interface ModeChoiceInput {
  today: PlainDate;
  /** Остаток третьего слоя целиком: «свободные траты» + цели + нераспределённое. */
  rest: Money;
  goals: readonly Goal[];
  /** Сколько по каждой цели ещё не внесено в этом месяце. */
  remaining: Record<string, Money>;
}

/** Сколько цели не хватает до её полной суммы. */
const toTarget = (g: Goal): Money => clampPos(sub(g.targetAmount, g.currentAmount));

/**
 * Три варианта на одних и тех же данных.
 *
 * Порядок расчёта повторяет жизнь, а не арифметику долей:
 *
 * 1. **Договор.** Невыполненная часть месячного взноса выполняется первой и
 *    одинаково во всех режимах: человек уже пообещал её себе, и режим тут
 *    ничего не решает.
 * 2. **Остаток делится по режиму.** Доля «свободные траты» — из общей таблицы.
 * 3. **Ускорение — только туда, где его примут.** Цель с фиксированным
 *    взносом сверх договора не получает: поднять взнос можно, но это
 *    отдельное решение человека, а не побочный эффект выбора режима. Цель
 *    «сколько останется» (`residual`) принимает сколько дадут — в пределах
 *    остатка до своей полной суммы.
 * 4. **Что не принято никем — нераспределённое.** Оно называется вслух:
 *    деньги не должны исчезать из таблицы.
 */
export function modeChoice(input: ModeChoiceInput): ModeChoice {
  const active = orderGoals(input.goals.filter((g) => g.status === 'active'));

  // Шаг 1: договор. Одинаков во всех режимах и ограничен деньгами.
  let left = input.rest;
  const committedBy = new Map<string, Money>();
  for (const g of active) {
    const owed = minM(input.remaining[g.id] ?? ZERO, toTarget(g));
    const take = minM(owed, left);
    if (take > ZERO) {
      committedBy.set(g.id, take);
      left = sub(left, take);
    }
  }
  const committed = sub(input.rest, left);

  // Сколько ускорения цели вообще способны принять.
  const capacity = add(...active
    .filter((g) => g.contributionMode === 'residual')
    .map((g) => clampPos(sub(toTarget(g), committedBy.get(g.id) ?? ZERO))));

  const outlooks = MODES.map((mode) => {
    const self = selfShare(left, mode);
    let pot = sub(left, self);

    const byGoal: ModeOutlook['byGoal'] = [];
    let extra: Money = ZERO;
    for (const g of active) {
      const base = committedBy.get(g.id) ?? ZERO;
      let add_ = ZERO;
      let why: string | undefined;

      if (g.contributionMode === 'residual') {
        const room = clampPos(sub(toTarget(g), base));
        add_ = minM(room, pot);
        pot = sub(pot, add_);
        if (room === ZERO) why = 'Цель уже собрана';
      } else if (base < (input.remaining[g.id] ?? ZERO)) {
        why = 'Денег хватило не на весь взнос этого месяца';
      } else {
        why = 'Получает принятый месячный взнос; больше — только по твоему решению';
      }

      const amount = add(base, add_);
      extra = add(extra, add_);
      byGoal.push({
        goalId: g.id,
        name: g.name,
        amount,
        eta: goalEta(g, amount > ZERO ? amount : g.monthlyContribution, input.today),
        ...(why ? { why } : {}),
      });
    }

    const goals = add(committed, extra);
    return {
      mode,
      title: MODE_TITLE[mode],
      self,
      goals,
      // Сходимость проверяется вычитанием, а не доверием: остаток третьего
      // слоя минус то, что разошлось по строкам.
      unallocated: sub(input.rest, add(self, goals)),
      byGoal,
    };
  });

  return {
    outlooks,
    rest: input.rest,
    committed,
    accelerates: capacity > ZERO && left > ZERO,
    hasGoals: active.some((g) => (input.remaining[g.id] ?? ZERO) > ZERO || toTarget(g) > ZERO),
  };
}

/**
 * Обоснованная рекомендация — или её отсутствие.
 *
 * Постоянный бейдж «рекомендуем» у «Баланса» был неправдой: это значение по
 * умолчанию, а не совет, посчитанный по чьей-то жизни. Совет уместен только
 * тогда, когда его можно объяснить числами самого человека.
 */
export function recommendedMode(input: {
  goals: readonly Goal[];
  /** Обязательные платежи обеспечены. */
  obligationsCovered: boolean;
  /** Есть ли куда ускоряться. */
  accelerates: boolean;
}): { mode: PlanMode; why: string } | null {
  const buffer = input.goals.find((g) => g.kind === 'emergency_fund' && g.status === 'active');
  if (!buffer || !input.obligationsCovered) return null;
  const left = clampPos(sub(buffer.targetAmount, buffer.currentAmount));
  if (left <= ZERO) return null;
  return {
    mode: input.accelerates ? 'goals_first' : 'balanced',
    why: `обязательные платежи обеспечены, а подушке не хватает ${formatMoney(left)}`,
  };
}
